// The pincode gate in the browser (issue 28): looks a pincode up, remembers the menu it gets,
// puts it on the cart, keeps the other menu out of sight, and stops mixed-region carts.
// The rules themselves are in cck-region-core.js; the header's forms send `cck:pincode` events
// and listen for the `cck:region` events this file sends.
import {
  hrefRegion,
  isPincode,
  lookup,
  nearestPincode,
  regionHref,
  regionLabel,
} from "@cck/region-core";

// cck:outcome holds the last served lookup (region, pincode, band and fee, or city).
const KEY = { outcome: "cck:outcome", gate: "cck:gate-dismissed" };

/** @returns {{ pincodesUrl: string, extras: import("./cck-region-core.js").Extra[], regionCollections?: string[], page: { region: string | null, counterpart: string | null } }} */
function pageData() {
  const el = document.getElementById("cck-region-data");
  try {
    return JSON.parse(el?.textContent ?? "{}");
  } catch {
    return { pincodesUrl: "", extras: [], page: { region: null, counterpart: null } };
  }
}

const store = {
  get(/** @type {string} */ k) {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(/** @type {string} */ k, /** @type {string} */ v) {
    try {
      localStorage.setItem(k, v);
    } catch {
      // Storage blocked (private mode): the choice lasts for this page only.
    }
  },
};
const session = {
  get: (/** @type {string} */ k) => {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (/** @type {string} */ k, /** @type {string} */ v) => {
    try {
      sessionStorage.setItem(k, v);
    } catch {
      // ignore
    }
  },
};

const data = pageData();
/** @type {Promise<import("./cck-region-core.js").Lists> | null} */
let listsPromise = null;
const lists = () =>
  (listsPromise ??= fetch(data.pincodesUrl).then((r) => {
    if (!r.ok) throw new Error(`pincode list: HTTP ${r.status}`);
    return r.json();
  }));

/** The last served outcome, or null before any pincode. */
function savedOutcome() {
  try {
    const o = JSON.parse(store.get(KEY.outcome) ?? "null");
    return o && (o.region === "ncr" || o.region === "national") ? o : null;
  } catch {
    return null;
  }
}

/** The menu the customer is on: "ncr", "national", or null before any pincode. */
export function currentRegion() {
  return savedOutcome()?.region ?? null;
}

// ---- Keeping the other menu out of sight -----------------------------------------------------

const CARD = "li, product-card, .product-card, article, .predictive-search__item";

function applyRegion(/** @type {"ncr" | "national"} */ region) {
  const other = region === "ncr" ? "national" : "ncr";
  // Every write below makes the browser restyle the page, so only write what changes
  // (issue 36: this ran on each DOM change and was most of the page's own blocking time).
  if (document.documentElement.dataset.cckRegion !== region)
    document.documentElement.dataset.cckRegion = region;
  /** @type {Set<Element>} */
  const hide = new Set();
  for (const a of document.querySelectorAll('a[href*="/collections/"], a[href*="/products/"]')) {
    const href = a.getAttribute("href") ?? "";
    if (href.includes("/collections/")) {
      const next = regionHref(href, region, data.regionCollections ?? []);
      if (next !== href) a.setAttribute("href", next);
    } else if (hrefRegion(href) === other && !a.closest("[data-cck-keep]")) {
      // Product cards from the other menu (collections, search, recommendations).
      hide.add(a.closest(CARD) ?? a);
    }
  }
  // Bring back the cards hidden for the old menu, when the customer switches.
  for (const el of document.querySelectorAll(".cck-other-region"))
    if (!hide.has(el)) el.classList.remove("cck-other-region");
  for (const el of hide)
    if (!el.classList.contains("cck-other-region")) el.classList.add("cck-other-region");
}

function watchForCards() {
  // Cards added later (Shopify's and Horizon's scripts add markup while the page loads): one
  // pass per frame, however many changes arrive in it.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      applyRegion(currentRegion() ?? "ncr");
    });
  }).observe(document.body, { childList: true, subtree: true });
}

/** On a product or collection page of the other menu: go to this menu's version, or say so. */
function guardPage(/** @type {"ncr" | "national"} */ region) {
  const { page } = data;
  if (!page?.region || page.region === region) return hideNotice();
  if (page.counterpart) {
    location.replace(page.counterpart + location.search + location.hash);
    return;
  }
  showNotice(
    region === "ncr"
      ? "This one is on my national menu only, so it can't come to your pincode."
      : "This one is on my Delhi NCR menu only, so it can't travel to your pincode.",
  );
}

function showNotice(/** @type {string} */ text) {
  const el = document.querySelector("[data-cck-region-notice]");
  if (!el) return;
  el.textContent = text;
  el.hidden = false;
}
function hideNotice() {
  const el = document.querySelector("[data-cck-region-notice]");
  if (el) el.hidden = true;
}

// ---- The cart ------------------------------------------------------------------------------

async function cartRegions() {
  const cart = await fetch("/cart.js").then((r) => r.json());
  return {
    count: cart.item_count ?? 0,
    regions: new Set(
      (cart.items ?? []).map((/** @type {{ handle: string }} */ i) =>
        hrefRegion(`/products/${i.handle}`),
      ),
    ),
  };
}

async function postCart(/** @type {string} */ path, /** @type {object} */ body) {
  await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
}

// ---- Choosing a pincode --------------------------------------------------------------------

function showNotServed(/** @type {string} */ pincode) {
  session.set("cck:not-served", pincode);
  for (const box of document.querySelectorAll("[data-cck-not-served]")) {
    box.hidden = false;
    for (const pin of box.querySelectorAll("[data-cck-not-served-pin]")) pin.textContent = pincode;
    const field = box.querySelector('input[name="contact[pincode]"]');
    if (field instanceof HTMLInputElement) field.value = pincode;
    const wa = box.querySelector("[data-cck-whatsapp]");
    if (wa instanceof HTMLAnchorElement) {
      wa.href = `https://wa.me/919873648122?text=${encodeURIComponent(`Hi! Can you deliver to ${pincode}?`)}`;
    }
  }
}
function hideNotServed() {
  for (const box of document.querySelectorAll("[data-cck-not-served]")) box.hidden = true;
}

/** Sends the outcome to the header and to anything else listening (homepage menu, issue 30). */
function announce(/** @type {import("./cck-region-core.js").Outcome} */ outcome) {
  // Scripts that load after this one read the latest outcome here instead of the first event.
  /** @type {any} */ (window).CCK_OUTCOME = outcome;
  document.dispatchEvent(
    new CustomEvent("cck:region", {
      detail: {
        ...outcome,
        region: outcome.region === "none" ? null : outcome.region,
        label: regionLabel(outcome),
        outcome,
      },
    }),
  );
}

async function choose(/** @type {string} */ pincode) {
  const outcome = lookup(pincode, await lists(), data.extras ?? []);
  if (outcome.region === "none") {
    showNotServed(pincode);
    announce(outcome);
    return outcome;
  }
  const previous = currentRegion();
  if (previous !== outcome.region) {
    const { count, regions } = await cartRegions();
    const other = outcome.region === "ncr" ? "national" : "ncr";
    if (count > 0 && regions.has(other)) {
      const ok = await askSwitch(
        `Your cart has items from my ${other === "ncr" ? "Delhi NCR" : "national"} menu, which can't go to ${pincode}.`,
      );
      if (!ok) {
        // Kept the old menu: put its pincode back in the boxes.
        const kept = savedOutcome();
        if (kept) announce(kept);
        return null;
      }
      await postCart("/cart/clear.js", {});
    }
  }
  hideNotServed();
  store.set(KEY.outcome, JSON.stringify(outcome));
  await postCart("/cart/update.js", {
    attributes: { Pincode: pincode, Menu: outcome.region === "ncr" ? "Delhi NCR" : "National" },
  });
  applyRegion(outcome.region);
  announce(outcome);
  closeGate();
  guardPage(outcome.region);
  return outcome;
}

// ---- "Use my location" ---------------------------------------------------------------------

function useLocation(/** @type {HTMLButtonElement} */ button) {
  const status = button.closest("form")?.querySelector("[data-cck-location-status]");
  const say = (/** @type {string} */ t) => {
    if (status) status.textContent = t;
  };
  if (!("geolocation" in navigator))
    return say("Your browser can't share a location. Type your pincode instead.");
  button.disabled = true;
  say("Finding you…");
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      button.disabled = false;
      const near = nearestPincode([pos.coords.latitude, pos.coords.longitude], await lists());
      if (!near) {
        say("You look outside the places I deliver to. Type your pincode to be sure.");
        return;
      }
      const input = button.closest("form")?.querySelector("[data-cck-pincode-input]");
      if (input instanceof HTMLInputElement) input.value = near.pincode;
      say(`Looks like ${near.pincode}. Change it if that's not right.`);
      await choose(near.pincode);
    },
    () => {
      button.disabled = false;
      say("I couldn't get your location. Type your pincode instead.");
    },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
  );
}

// ---- The first-visit gate ------------------------------------------------------------------

function gate() {
  const d = document.querySelector("[data-cck-gate]");
  return d instanceof HTMLDialogElement ? d : null;
}
function openGate() {
  const d = gate();
  if (d && !d.open) d.showModal();
}
function closeGate() {
  const d = gate();
  if (d?.open) d.close();
}

/**
 * Switching menus empties the cart: asked in the site's own dialog (issue 186), not the
 * browser's. Resolves true to empty the cart and switch.
 * @param {string} why
 * @returns {Promise<boolean>}
 */
function askSwitch(why) {
  const d = document.createElement("dialog");
  d.className = "cck-gate cck-confirm";
  d.setAttribute("aria-labelledby", "cck-confirm-title");
  d.innerHTML = `<form method="dialog">
      <p class="cck-hand cck-gate__kicker">one menu at a time</p>
      <h2 class="cck-gate__title" id="cck-confirm-title">Switch menus?</h2>
      <p class="cck-gate__text"></p>
      <div class="cck-confirm__actions">
        <button value="switch" class="cck-amber-button">Empty the cart and switch</button>
        <button value="keep" class="cck-link-button">Keep my cart</button>
      </div>
    </form>`;
  /** @type {HTMLElement} */ (d.querySelector(".cck-gate__text")).textContent =
    `${why} Each order comes from one menu.`;
  document.body.append(d);
  return new Promise((resolve) => {
    d.addEventListener("close", () => {
      resolve(d.returnValue === "switch");
      d.remove();
    });
    d.showModal();
    /** @type {HTMLButtonElement} */ (d.querySelector('button[value="keep"]')).focus();
  });
}

// ---- Wiring --------------------------------------------------------------------------------

document.addEventListener("cck:pincode", (e) => {
  const { pincode } = /** @type {CustomEvent} */ (e).detail ?? {};
  if (isPincode(pincode))
    choose(pincode).catch(() =>
      showNotice("Something went wrong checking that pincode. Please try again."),
    );
});

for (const b of document.querySelectorAll("[data-cck-use-location]")) {
  if (b instanceof HTMLButtonElement) b.addEventListener("click", () => useLocation(b));
}

gate()
  ?.querySelector("[data-cck-gate-browse]")
  ?.addEventListener("click", () => {
    session.set(KEY.gate, "1");
    closeGate();
  });
// Esc (or the browser's back gesture on a dialog) means "just browsing" too, for the visit
// (issue 183): the gate doesn't come back on the next page.
gate()?.addEventListener("cancel", () => session.set(KEY.gate, "1"));

// Once the pincode is known, the bar becomes one line: "Delivering to 201304 · Delhi NCR menu ·
// Change" (issue 183). Change brings the form back.
const header = document.querySelector("[data-cck-header]");
const compactLine = document.querySelector("[data-cck-pincode-compact]");
function showCompact(/** @type {any} */ outcome) {
  if (!(header instanceof HTMLElement) || !(compactLine instanceof HTMLElement)) return;
  const served = outcome && (outcome.region === "ncr" || outcome.region === "national");
  compactLine.hidden = !served;
  header.classList.toggle("cck-header--known", Boolean(served));
  if (!served) return;
  const pin = compactLine.querySelector("[data-cck-compact-pin]");
  const menu = compactLine.querySelector("[data-cck-compact-menu]");
  if (pin) pin.textContent = outcome.pincode ?? "";
  if (menu) menu.textContent = regionLabel(outcome);
}
document.addEventListener("cck:region", (e) =>
  showCompact(/** @type {CustomEvent} */ (e).detail?.outcome),
);
compactLine?.querySelector("[data-cck-pincode-change]")?.addEventListener("click", () => {
  header?.classList.remove("cck-header--known");
  if (compactLine instanceof HTMLElement) compactLine.hidden = true;
  const input = header?.querySelector(".cck-pincode-bar [data-cck-pincode-input]");
  if (input instanceof HTMLInputElement) input.focus();
});

// Before anything goes in the cart, the customer must have a menu that matches the product:
// both "Add to cart" (a form submit) and Shopify's "Buy it now" button (a click that skips the
// cart) wait for the gate.
// On window, in the capture phase, so this runs before Horizon's own handlers (they listen on
// document).
const needsGate = () => {
  const region = currentRegion();
  return !region || Boolean(data.page?.region && data.page.region !== region);
};
window.addEventListener(
  "submit",
  (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!(form.getAttribute("action") ?? "").includes("/cart/add") || !needsGate()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    openGate();
  },
  true,
);
window.addEventListener(
  "click",
  (e) => {
    const t = e.target;
    if (
      !(t instanceof Element) ||
      !t.closest(
        '.shopify-payment-button, shopify-accelerated-checkout, form[action*="/cart/add"] [type="submit"]',
      )
    )
      return;
    if (!needsGate()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    openGate();
  },
  true,
);

// "Tell me when": the request goes to CCK Ops (issue 41), where it waits in Delivery areas. If
// that fails, the form posts as it is (Shopify's contact form, which emails the store).
for (const form of document.querySelectorAll("form[data-cck-request]")) {
  if (!(form instanceof HTMLFormElement)) continue;
  let sending = false;
  form.addEventListener("submit", async (e) => {
    if (sending) return;
    e.preventDefault();
    const data = new FormData(form);
    const pincode = String(data.get("contact[pincode]") ?? "");
    const email = String(data.get("contact[email]") ?? "");
    try {
      const body = new FormData();
      body.set("pincode", pincode);
      body.set("email", email);
      const res = await fetch(form.dataset.cckRequest ?? "", { method: "POST", body });
      const r = await res.json();
      if (!res.ok || !r.ok) throw new Error(r.error ?? `HTTP ${res.status}`);
      // style, not the hidden attribute: the amber button's display rule would win over [hidden].
      for (const el of form.querySelectorAll("input:not([type=hidden]), button")) {
        if (el instanceof HTMLElement) el.style.display = "none";
      }
      const thanks = form.querySelector("[data-cck-request-thanks]");
      if (thanks instanceof HTMLElement) thanks.hidden = false;
    } catch {
      sending = true;
      form.submit();
    }
  });
}

// After a pincode request is sent, Shopify reloads the page with ?contact_posted=true:
// show the thank-you in the bar's panel.
if (new URLSearchParams(location.search).has("contact_posted")) {
  showNotServed(session.get("cck:not-served") ?? "");
  const gateBox = gate()?.querySelector("[data-cck-not-served]");
  if (gateBox instanceof HTMLElement) gateBox.hidden = true;
}

const saved = savedOutcome();
if (saved) {
  applyRegion(saved.region);
  announce(saved);
  guardPage(saved.region);
} else {
  // Until a pincode is given, show the Delhi NCR menu, and ask once per visit: by itself only
  // where the menus are browsed (the home page, the menus). Elsewhere (a product someone sent,
  // a blog post) the pincode bar asks, and the gate waits for "Add to cart" (issue 183).
  applyRegion("ncr");
  const browsing = location.pathname === "/" || location.pathname.startsWith("/collections");
  if (!session.get(KEY.gate) && browsing) openGate();
}
watchForCards();
